import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Minus, Plus, Loader2, Check, Sparkles, ChevronDown, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { Drawer, DrawerContent, DrawerTitle } from '../../components/ui/drawer';
import { MACRO_META } from './trackingConstants';

const MACRO_KEYS = ['protein', 'carbs', 'fat'];
const KCAL_PER_G = { protein: 4, carbs: 4, fat: 9 };
const MIN_CAL = 800;
const MAX_CAL = 6000;
const QUICK_JUMPS = [10, 20, 25, 30, 35, 40, 45, 50, 60];

const PRESETS = [
  { key: 'balanced', fr: 'Équilibré', en: 'Balanced', pct: { protein: 30, carbs: 40, fat: 30 } },
  { key: 'high_protein', fr: 'Riche en protéines', en: 'High protein', pct: { protein: 35, carbs: 35, fat: 30 } },
  { key: 'low_carb', fr: 'Faible en glucides', en: 'Low carb', pct: { protein: 35, carbs: 20, fat: 45 } },
];

/** Barre unique : les 3 macros se partagent toujours EXACTEMENT 100% de largeur.
 * Si le total dépasse 100%, la portion excédentaire est représentée en rouge hachuré
 * (au lieu de déborder hors du conteneur), pour rester lisible dans tous les cas. */
const StackedProgressBar = ({ pct, totalPct }) => {
  const displayTotal = Math.max(totalPct, 100);
  const w = (v) => (v / displayTotal) * 100;
  const overflow = Math.max(0, totalPct - 100);
  return (
    <div className="h-4 rounded-full overflow-hidden flex w-full bg-zinc-800/60 border border-white/10">
      {MACRO_KEYS.map((k) => (
        <motion.div key={k} className="h-full" animate={{ width: `${w(pct[k])}%` }} transition={{ duration: 0.25 }} style={{ background: MACRO_META[k].color }} />
      ))}
      {overflow > 0 && (
        <motion.div className="h-full" animate={{ width: `${w(overflow)}%` }} transition={{ duration: 0.25 }}
          style={{ backgroundImage: 'repeating-linear-gradient(45deg, rgba(244,63,94,0.95) 0 6px, rgba(244,63,94,0.5) 6px 12px)' }} />
      )}
    </div>
  );
};

/** Ligne macro : pill tactile "35% ▾" qui ouvre le Precision Drawer — bien plus précis
 * qu'un slider inline sur mobile (impossible de rater le chiffre exact). */
const MacroRow = ({ k, pct, grams, en, onOpen }) => (
  <button onClick={() => onOpen(k)} className="w-full flex items-center justify-between py-3 border-b border-white/5 last:border-0 active:scale-[0.99] transition-transform" data-testid={`drawer-trigger-${k}`}>
    <span className="flex items-center gap-2 text-sm font-semibold text-zinc-200">
      <span className="w-2.5 h-2.5 rounded-full" style={{ background: MACRO_META[k].color }} />
      {en ? MACRO_META[k].en : MACRO_META[k].fr}
      <span className="text-xs text-zinc-500 font-normal">· {grams}g</span>
    </span>
    <span className="inline-flex items-center gap-1 pl-3 pr-2 py-1.5 rounded-full bg-zinc-800 border border-white/10 text-sm font-bold text-white tabular-nums">
      {pct}% <ChevronDown className="w-3.5 h-3.5 text-zinc-400" />
    </span>
  </button>
);

export const MacroOverridePanel = ({ initial, onSave, onCancel, busy, en, api }) => {
  const [caloriesInput, setCaloriesInput] = useState(String(initial.calories || 2000));
  const [pct, setPct] = useState(initial.pct);
  const [activeMacro, setActiveMacro] = useState(null);
  const [floor, setFloor] = useState(1200);

  React.useEffect(() => {
    if (!api) return;
    api('/api/tracking/profile').then((d) => setFloor(d.sex === 'male' ? 1500 : 1200)).catch(() => {});
  }, [api]);

  const calories = parseInt(caloriesInput, 10) || 0;
  const lowCalorieWarning = calories > 0 && calories < floor;
  const totalPct = pct.protein + pct.carbs + pct.fat;
  const totalValid = totalPct === 100;

  const grams = MACRO_KEYS.reduce((acc, k) => {
    acc[k] = calories > 0 ? Math.max(0, Math.round((calories * pct[k]) / 100 / KCAL_PER_G[k])) : 0;
    return acc;
  }, {});

  const bumpCalories = (delta) => setCaloriesInput((c) => String(Math.max(MIN_CAL, Math.min(MAX_CAL, (parseInt(c, 10) || 0) + delta))));
  const handleCaloriesBlur = () => {
    const v = parseInt(caloriesInput, 10);
    if (isNaN(v)) { setCaloriesInput(String(initial.calories || 2000)); return; }
    setCaloriesInput(String(Math.max(MIN_CAL, Math.min(MAX_CAL, v))));
  };

  const setMacro = (k, rawVal) => setPct((p) => ({ ...p, [k]: Math.max(0, Math.min(100, Math.round(Number(rawVal)))) }));
  const bumpMacro = (k, delta) => setMacro(k, pct[k] + delta);

  const save = () => {
    if (isNaN(calories) || calories < MIN_CAL || calories > MAX_CAL) {
      toast.error(en ? `Calories must be between ${MIN_CAL} and ${MAX_CAL}` : `Les calories doivent être entre ${MIN_CAL} et ${MAX_CAL}`);
      return;
    }
    onSave({ calories, protein_pct: pct.protein, carbs_pct: pct.carbs, fat_pct: pct.fat });
  };

  return (
    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
      className="relative z-[60] overflow-hidden mt-4" data-testid="macro-override-panel">
      <div className="rounded-2xl bg-gradient-to-br from-zinc-900 to-black border border-primary/20 p-5">
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="w-4 h-4 text-yellow-400" />
          <h4 className="text-sm font-bold text-white">{en ? 'Custom targets' : 'Objectifs personnalisés'}</h4>
        </div>

        {/* Calories cibles — stepper tactile */}
        <div className="mb-5">
          <label className="text-xs text-zinc-400 mb-1.5 block">{en ? 'Target calories' : 'Calories cibles'}</label>
          <div className="flex items-center gap-3">
            <button onClick={() => bumpCalories(-50)} aria-label={en ? 'Decrease calories' : 'Diminuer les calories'}
              className="w-10 h-10 rounded-xl bg-white/5 backdrop-blur border border-white/10 border-t-white/20 text-white flex items-center justify-center hover:bg-white/10 active:scale-95 transition-all" data-testid="calories-minus-btn">
              <Minus className="w-4 h-4" />
            </button>
            <input
              type="text" inputMode="numeric" value={caloriesInput}
              onChange={(e) => { if (/^\d*$/.test(e.target.value)) setCaloriesInput(e.target.value); }}
              onBlur={handleCaloriesBlur}
              className="w-32 text-center bg-transparent border-0 py-1 text-4xl font-black text-white tabular-nums focus:outline-none"
              data-testid="calories-override-input" />
            <span className="text-xs text-zinc-500">kcal</span>
            <button onClick={() => bumpCalories(50)} aria-label={en ? 'Increase calories' : 'Augmenter les calories'}
              className="w-10 h-10 rounded-xl bg-white/5 backdrop-blur border border-white/10 border-t-white/20 text-white flex items-center justify-center hover:bg-white/10 active:scale-95 transition-all" data-testid="calories-plus-btn">
              <Plus className="w-4 h-4" />
            </button>
          </div>
          {lowCalorieWarning && (
            <p className="flex items-start gap-1.5 text-xs text-amber-300 mt-2" data-testid="low-calorie-warning">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              {en
                ? 'Very low target — this should be validated by a health professional. Your coach will be notified.'
                : 'Objectif très bas — à valider idéalement avec un professionnel de santé. Votre coach en sera informé.'}
            </p>
          )}
        </div>

        {/* Répartition macro */}
        <div className="mb-2 flex items-center justify-between">
          <label className="text-xs text-zinc-400">{en ? 'Macro split · tap to edit' : 'Répartition des macros · touchez pour éditer'}</label>
          <span className={`text-xs font-bold tabular-nums ${totalValid ? 'text-emerald-400' : 'text-rose-400'}`} data-testid="macro-total-badge">
            {en ? 'Total' : 'Total'} : {totalPct}%
          </span>
        </div>

        <StackedProgressBar pct={pct} totalPct={totalPct} />
        {!totalValid && (
          <p className="text-xs text-rose-400 mt-1.5 mb-1" role="alert" data-testid="macro-total-warning">
            {totalPct < 100
              ? (en ? `Add ${100 - totalPct}% to reach 100%` : `Ajoutez ${100 - totalPct}% pour atteindre 100%`)
              : (en ? `Overshooting by ${totalPct - 100}% — remove it to reach 100%` : `Dépassement de ${totalPct - 100}% — retirez-le pour atteindre 100%`)}
          </p>
        )}

        <div className="mt-3">
          {MACRO_KEYS.map((k) => (
            <MacroRow key={k} k={k} pct={pct[k]} grams={grams[k]} en={en} onOpen={setActiveMacro} />
          ))}
        </div>

        {/* Préréglages rapides */}
        <div className="flex flex-wrap gap-2 mt-5">
          {PRESETS.map((p) => (
            <button key={p.key} onClick={() => setPct(p.pct)}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold text-zinc-300 border border-white/10 hover:border-primary/50 hover:text-white transition-colors active:scale-95"
              data-testid={`macro-preset-${p.key}`}>
              {en ? p.en : p.fr}
            </button>
          ))}
        </div>

        <div className="flex gap-2 mt-5">
          <button onClick={save} disabled={busy || !totalValid}
            className="flex-1 py-2.5 rounded-lg font-bold text-white bg-gradient-to-r from-primary to-pink-500 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            data-testid="save-goals-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} {en ? 'Save' : 'Enregistrer'}
          </button>
          <button onClick={onCancel} className="px-4 py-2.5 rounded-lg text-sm font-semibold text-zinc-300 border border-white/10 hover:text-white transition-colors" data-testid="cancel-goals-btn">
            {en ? 'Cancel' : 'Annuler'}
          </button>
        </div>
      </div>

      {/* Precision Drawer : édition tactile ultra précise d'une macro (bottom sheet) */}
      <Drawer open={!!activeMacro} onOpenChange={(o) => !o && setActiveMacro(null)}>
        <DrawerContent className="bg-zinc-950 border-white/10 backdrop-blur-2xl" data-testid="macro-precision-drawer">
          {activeMacro && (
            <div className="px-6 pb-8 pt-2 max-w-md mx-auto w-full">
              <DrawerTitle className="flex items-center gap-2 text-sm font-bold text-zinc-300 justify-center mb-2">
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: MACRO_META[activeMacro].color }} />
                {en ? MACRO_META[activeMacro].en : MACRO_META[activeMacro].fr}
              </DrawerTitle>

              <div className="flex items-center justify-center gap-5 my-4">
                <button onClick={() => bumpMacro(activeMacro, -1)} aria-label={en ? 'Decrease' : 'Diminuer'}
                  className="w-14 h-14 rounded-2xl bg-white/5 backdrop-blur border border-white/10 border-t-white/20 text-white flex items-center justify-center hover:bg-white/10 active:scale-90 transition-all" data-testid="stepper-minus-btn">
                  <Minus className="w-6 h-6" />
                </button>
                <span className="text-6xl font-black tabular-nums text-white w-36 text-center" style={{ color: MACRO_META[activeMacro].color }} data-testid="drawer-active-value">
                  {pct[activeMacro]}%
                </span>
                <button onClick={() => bumpMacro(activeMacro, 1)} aria-label={en ? 'Increase' : 'Augmenter'}
                  className="w-14 h-14 rounded-2xl bg-white/5 backdrop-blur border border-white/10 border-t-white/20 text-white flex items-center justify-center hover:bg-white/10 active:scale-90 transition-all" data-testid="stepper-plus-btn">
                  <Plus className="w-6 h-6" />
                </button>
              </div>

              <p className="text-center text-sm text-zinc-500 mb-5">{grams[activeMacro]}g {en ? 'per day' : 'par jour'}</p>

              <input
                type="range" min={0} max={100} value={pct[activeMacro]}
                onChange={(e) => setMacro(activeMacro, e.target.value)}
                className="w-full h-2 rounded-full appearance-none cursor-pointer slider-primary mb-5"
                style={{ background: `linear-gradient(90deg, ${MACRO_META[activeMacro].color} ${pct[activeMacro]}%, rgba(255,255,255,0.08) ${pct[activeMacro]}%)` }}
                data-testid={`macro-slider-${activeMacro}`}
              />

              <div className="flex flex-wrap gap-2 justify-center mb-6">
                {QUICK_JUMPS.map((v) => (
                  <button key={v} onClick={() => setMacro(activeMacro, v)}
                    className={`w-11 h-9 rounded-lg text-xs font-bold border transition-all active:scale-95 ${pct[activeMacro] === v ? 'text-white border-transparent' : 'text-zinc-400 border-white/10 hover:border-white/30'}`}
                    style={pct[activeMacro] === v ? { background: MACRO_META[activeMacro].color } : {}}
                    data-testid={`quick-jump-${activeMacro}-${v}`}>
                    {v}%
                  </button>
                ))}
              </div>

              <button onClick={() => setActiveMacro(null)}
                className="w-full py-3 rounded-xl font-bold text-white bg-gradient-to-r from-primary to-pink-500 active:scale-[0.98] transition-transform"
                data-testid="drawer-validate-btn">
                {en ? 'Done' : 'Valider'}
              </button>
            </div>
          )}
        </DrawerContent>
      </Drawer>
    </motion.div>
  );
};

export default MacroOverridePanel;
